import {useCallback, useEffect, useRef} from "react";
import {PublicSurveyIdentity, upsertPublicSurveySession} from "./publicSurvey.api";

const SESSION_THROTTLE_MS = 2000;

type Options = {
    identity: PublicSurveyIdentity
    version?: number
};

const usePublicSurveySession = ({identity, version}: Options) => {
    const sessionIdRef = useRef<string | undefined>(undefined);
    const furthestRef = useRef<{index: number, questionId?: string}>({index: -1});

    const startedRef = useRef(false);
    const timerRef = useRef<number | undefined>(undefined);
    const inFlightRef = useRef(false);
    const dirtyRef = useRef(false);

    const identityRef = useRef(identity);
    const versionRef = useRef(version);
    useEffect(() => {
        identityRef.current = identity;
        versionRef.current = version;
    });

    const flush = useCallback(() => {
        if (inFlightRef.current) {
            dirtyRef.current = true;
            return;
        }
        inFlightRef.current = true;

        const furthest = furthestRef.current;

        upsertPublicSurveySession({
            ...identityRef.current,
            sessionId: sessionIdRef.current,
            version: versionRef.current,
            furthestQuestionId: furthest.questionId,
        })
            .then(response => {
                if (response?.sessionId) sessionIdRef.current = response.sessionId;
            })
            .catch(() => undefined)
            .then(() => {
                inFlightRef.current = false;
                if (!dirtyRef.current) return;

                dirtyRef.current = false;
                flush();
            });
    }, []);

    const track = useCallback((questionId: string | undefined, index: number) => {
        const moved = index > furthestRef.current.index;
        if (moved) furthestRef.current = {index, questionId};

        if (!startedRef.current) {
            startedRef.current = true;
            flush();
            return;
        }

        if (!moved || timerRef.current !== undefined) return;

        timerRef.current = window.setTimeout(() => {
            timerRef.current = undefined;
            flush();
        }, SESSION_THROTTLE_MS);
    }, [flush]);

    useEffect(() => () => {
        if (timerRef.current === undefined) return;

        window.clearTimeout(timerRef.current);
        timerRef.current = undefined;
        flush();
    }, [flush]);

    return {sessionIdRef, track};
};

export default usePublicSurveySession;
